import { useAccountUsers } from '@/api/queries/me/useAccountUsers';
import { useRegion } from '@/api/rest/users/useRegion';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import UserIndicator from '@payaca/components/userIndicator/UserIndicator';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { AssigneeTimelogsGrouping as AssigneeTimelogsGroupingType } from '@payaca/types/listedTimelogTypes';
import {
  DurationUnit,
  getReadableStringFromDurationString,
} from '@payaca/utilities/timeUtilities';
import { FC, type JSX } from 'react';
import './AssigneeTimelogsGrouping.sass';
import TimelogRow from './TimelogRow';

type Props = {
  assigneeTimelogsGrouping: AssigneeTimelogsGroupingType;
  onUpdateTimelogSuccess?: () => void;
  onArchiveTimelogSuccess?: () => void;
};

const AssigneeTimelogsGrouping: FC<Props> = ({
  assigneeTimelogsGrouping,
  onUpdateTimelogSuccess,
  onArchiveTimelogSuccess,
}): JSX.Element | null => {
  const users = useAccountUsers();
  const user = users.find(
    (x) =>
      assigneeTimelogsGrouping.assignee.type == 'user' &&
      x.id === +assigneeTimelogsGrouping.assignee.publicId
  );

  const region = useRegion();

  return (
    <ResponsiveViewWrapper
      downBreakpointSm={800}
      downBreakpointXs={450}
      className="assignee-timelogs-grouping"
    >
      <div className="assignee-information">
        {user && (
          <div className="assignee-indicator">
            <UserIndicator
              user={{
                firstName: user?.firstname,
                lastName: user?.lastname,
                emailAddress: user?.email,
                userColour: user?.userColour,
                imgSrc: user?.avatarUrl,
              }}
              isUserInformationHoverPopperEnabled={false}
            />
          </div>
        )}
        <span className="assignee-name">
          {assigneeTimelogsGrouping.assignee.firstName}{' '}
          {assigneeTimelogsGrouping.assignee.lastName}
        </span>
      </div>
      <div className="assignee-totals">
        <dl>
          {/* Always render the total, even when zero, so the line doesn't
              disappear for a period with no logged time / only running timers. */}
          <dt>Time spent:</dt>
          <dd>
            {assigneeTimelogsGrouping.totalDuration
              ? getReadableStringFromDurationString(
                  assigneeTimelogsGrouping.totalDuration,
                  { units: [DurationUnit.HOURS, DurationUnit.MINUTES] }
                )
              : '0h 0m'}
          </dd>
          <dt>Cost:</dt>
          <dd>
            {currencyPrice(
              assigneeTimelogsGrouping.totalCalculatedCost,
              region
            )}
          </dd>
        </dl>
      </div>
      <div className="timelogs">
        <ul>
          {!assigneeTimelogsGrouping.timelogs.length && (
            <li>No timelogs to display</li>
          )}
          {assigneeTimelogsGrouping.timelogs.map((timelog, i) => {
            return (
              <li key={i}>
                <TimelogRow
                  timelog={timelog}
                  onUpdateTimelogSuccess={onUpdateTimelogSuccess}
                  onArchiveTimelogSuccess={onArchiveTimelogSuccess}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </ResponsiveViewWrapper>
  );
};

export default AssigneeTimelogsGrouping;
